<script setup lang="ts">
import {
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from 'reka-ui';
import { computed, onMounted, ref, useAttrs } from 'vue';

import { cnMerge } from '@/utils/utils.js';
import ChevronDownIcon from '~icons/ion/chevron-down-outline';
import EllipsisVerticalIcon from '~icons/ion/ellipsis-vertical';

import KaMenuGroup from './KaMenuGroup.vue';
import KaMenuItem from './KaMenuItem.vue';
import MenuContextProvider from './MenuContextProvider.vue';
import type { KaMenuProps } from './types';

export type { KaMenuProps } from './types';

const props = withDefaults(defineProps<KaMenuProps>(), {
  aligned: 'right',
  hideLabel: false,
  modal: false,
});

const attrs = useAttrs();

const id = (attrs.id as string) || props.label;
const dataTestid = (attrs['data-testid'] as string) || id;

const triggerRef = ref<HTMLButtonElement | null>(null);
const portalTo = ref<string | HTMLElement | undefined>(undefined);
onMounted(() => {
  const dialogAncestor = triggerRef.value?.closest<HTMLElement>('[role="dialog"]');
  if (dialogAncestor) {
    portalTo.value = dialogAncestor;
  } else if (document.getElementById('main-content')) {
    portalTo.value = '#main-content';
  }
});

const registeredMenuGroups = ref<Map<symbol, boolean>>(new Map());
const hasGroups = computed(() => registeredMenuGroups.value.size > 0);

const registerMenuGroup = (id: symbol) => {
  registeredMenuGroups.value.set(id, true);
};

const deregisterMenuGroup = (id: symbol) => {
  registeredMenuGroups.value.delete(id);
};

// Restore focus to the trigger before a menu item runs its action, so a dialog
// opened by that action records the persistent trigger as its focus-restore
// target rather than the menu item (which unmounts on close). See K10-36133.
const focusTrigger = () => {
  triggerRef.value?.focus();
};

// Defaults to an internal ref when a parent doesn't bind v-model:open, so
// this is a no-op change for every existing (uncontrolled) usage.
const open = defineModel<boolean>('open', { default: false });

// Lets a parent force the menu shut, eg. when its trigger is about to be
// hidden by a responsive breakpoint change (K10-29615) — reka-ui only closes
// on Escape/outside-click/select, not on the trigger disappearing.
const close = () => {
  open.value = false;
};

// Lets parent components move focus to this menu's trigger (e.g. as a fallback
// initial-focus target when another element they'd normally focus is hidden).
defineExpose({ focusTrigger, close });

const computedAlignment = computed(() => {
  return props.aligned === 'left' ? 'start' : props.aligned === 'right' ? 'end' : 'start';
});

const isIconOnlyTrigger = computed(() => !props.label || props.hideLabel);

const TRIGGER_ICON_WRAPPER_BASE = 'flex flex-shrink-0 items-center justify-center';
const TRIGGER_ICON_WRAPPER_PILL =
  'rounded-full bg-gray-100 p-1 text-gray-400 hover:bg-gray-200 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-200 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700';
</script>

<template>
  <DropdownMenuRoot v-model:open="open" :modal="props.modal" class="inline-block">
    <DropdownMenuTrigger as-child>
      <button
        ref="triggerRef"
        type="button"
        :data-testid="dataTestid"
        :aria-label="props.hideLabel ? props.label : undefined"
        :class="
          cnMerge(
            'flex items-center transition-colors',
            {
              'rounded bg-transparent px-4 py-2 text-left text-sm font-semibold capitalize text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 dark:bg-navy-800 dark:text-gray-200 dark:ring-white/5 dark:hover:bg-navy-700':
                !!props.label && !props.hideLabel,
            },
            props.buttonClasses,
          )
        "
        @click.stop
      >
        <span
          v-if="$slots.left || props.left"
          class="mr-2 h-5 w-5 flex-shrink-0"
          aria-hidden="true"
        >
          <slot name="left">
            <component
              :is="props.left.icon"
              v-if="props.left"
              :class="['h-full w-full', props.left.classes]"
            />
          </slot>
        </span>
        <span
          v-if="$slots.label || props.label"
          :class="[props.hideLabel ? 'sr-only' : 'block w-full truncate']"
          :title="props.label"
        >
          <slot name="label" :label="props.label">
            {{ props.label }}
          </slot>
        </span>
        <!--
          The wrapper carries aria-hidden so AT skips the decorative
          icon / slot subtree (it's redundant with the trigger button's
          own aria-label). The icons inside carry NO tabindex: SVG is
          not focusable by default in modern browsers, and tabindex="-1"
          was making them programmatically focusable, which let reka-ui
          / SortableJS / focus-visible heuristics land focus on the
          icon. Focus on a descendant of (or on the element itself
          when it had aria-hidden) tripped Chrome's "Blocked aria-hidden"
          warning. Removing tabindex makes .focus() on these elements
          silently no-op so focus stays on the trigger button.
        -->
        <span
          aria-hidden="true"
          :class="
            cnMerge(
              TRIGGER_ICON_WRAPPER_BASE,
              isIconOnlyTrigger
                ? [TRIGGER_ICON_WRAPPER_PILL, props.triggerIconWrapperClasses]
                : 'ml-2',
            )
          "
        >
          <slot name="right">
            <component
              :is="props.right.icon"
              v-if="props.right"
              :class="['h-5 w-5', props.right.classes]"
            />

            <template v-else>
              <ChevronDownIcon
                v-if="($slots.label || props.label) && !props.hideLabel"
                class="-mr-1 size-4 text-gray-500 outline-none focus:outline-none"
              />
              <EllipsisVerticalIcon v-else class="h-5 w-5 outline-none focus:outline-none" />
            </template>
          </slot>
        </span>
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuPortal :to="portalTo">
      <DropdownMenuContent
        as="ul"
        :align="computedAlignment"
        :side-offset="8"
        :class="
          cnMerge(
            'data-overflow-auto animate-slideDownAndFade rounded bg-white p-0 shadow-lg ring-1 ring-gray-300 focus:outline-none dark:bg-navy-800 dark:text-gray-50 dark:ring-navy-600',
            {
              'divide-y divide-gray-200 dark:divide-navy-600': hasGroups,
            },
            props.itemsClasses,
            props.itemsZIndexClass ?? 'z-20',
          )
        "
      >
        <MenuContextProvider
          :data-testid="dataTestid"
          :register-menu-group="registerMenuGroup"
          :deregister-menu-group="deregisterMenuGroup"
          :focus-trigger="focusTrigger"
        >
          <slot v-if="!props.items" />
          <template
            v-for="item in props.items"
            v-else
            :key="Array.isArray(item) ? `${item[0].label}-group` : item.label"
          >
            <KaMenuGroup v-if="Array.isArray(item)" :items="item" />

            <KaMenuItem
              v-else
              :label="item.label"
              :left="item.left"
              :right="item.right"
              :danger="item.danger"
              :disabled="item.disabled"
              :on-click="item.onClick"
              :hidden="item.hidden"
              :as="item.as"
              :link-props="item.as === 'link' ? item.linkProps : undefined"
              :button-classes="item.as === 'button' ? item.buttonClasses : undefined"
              :container-classes="item.containerClasses"
              :sr-only-label="item.srOnlyLabel"
            />
          </template>
        </MenuContextProvider>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
